// v2/components/admin/BulkCampaignModal.js — Excel ile toplu kampanya yukleme
// Akis: sablon indir -> dosya sec -> SUNUCU dogrulamasi (onizleme) -> onayla
// Dogrulama ve yazma sunucuda; yazma tek transaction (hepsi ya da hicbiri).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  X, Upload, Download, Loader2, AlertTriangle, CheckCircle2, FileSpreadsheet, Info,
} from 'lucide-react';
import { adminCampaignApi } from '../../hooks/useAdminCampaigns';
import { formatNumber } from '../../utils/format';
import s from './BulkCampaignModal.module.css';

const BASLIK = [
  'Kampanya Adı', 'Açıklama', 'Müşteri Kodu', 'Kampanya Tipi', 'Ürün/Ürün Grubu Kodu',
  'İndirim 1 (%)', 'İndirim 2 (%)', 'İndirim 3 (%)', 'İndirim 4 (%)', 'İndirim 5 (%)',
  'Başlangıç Tarihi (YYYY-MM-DD)', 'Bitiş Tarihi (YYYY-MM-DD)', 'Durum',
];
const ALAN = {
  'Kampanya Adı': 'name', 'Açıklama': 'description', 'Müşteri Kodu': 'code',
  'Kampanya Tipi': 'type', 'Ürün/Ürün Grubu Kodu': 'target',
  'Başlangıç Tarihi (YYYY-MM-DD)': 'start', 'Bitiş Tarihi (YYYY-MM-DD)': 'end',
  'Durum': 'status',
};
const ORAN_BASLIK = BASLIK.slice(5, 10);

const metin = (v) => (v === undefined || v === null ? '' : String(v).trim());

/** Excel hucresi -> 'YYYY-MM-DD' (tarih hucresi Date gelebilir) */
function tarih(v) {
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
  }
  return metin(v).slice(0, 10);
}

function satirlariCoz(sayfa) {
  const ham = XLSX.utils.sheet_to_json(sayfa, { header: 1, raw: false, cellDates: true });
  if (!ham.length) return [];
  const basliklar = (ham[0] || []).map((h) => metin(h));
  const out = [];
  ham.slice(1).forEach((r, i) => {
    if (!r || !r.some((h) => metin(h) !== '')) return;   // bos satiri atla
    const o = { row: i + 2, rates: [] };
    basliklar.forEach((b, j) => {
      const v = r[j];
      if (ALAN[b]) {
        o[ALAN[b]] = (b.indexOf('Tarihi') >= 0) ? tarih(v) : metin(v);
      } else if (ORAN_BASLIK.indexOf(b) >= 0 && metin(v) !== '') {
        o.rates.push(metin(v));
      }
    });
    if (!o.status) o.status = 'Aktif';
    out.push(o);
  });
  return out;
}

function sablonIndir(gruplar) {
  const grupSatirlari = (gruplar || []).length
    ? gruplar.map((g) => [`   • ${g.label}`])
    : [['   • (grup listesi yüklenemedi)']];
  const talimat = [
    ['AS OTOMOTİV — TOPLU KAMPANYA YÜKLEME ŞABLONU'], [''],
    ['1. "Kampanyalar" sayfasını doldurun. Örnek satırları silmeyi unutmayın.'],
    ['2. Kampanya Tipi: "Ürün" veya "Ürün Grubu" olmalıdır.'],
    ['3. Müşteri Kodu boş bırakılırsa kampanya TÜM müşterilere uygulanır.'],
    ['4. Tarihler YYYY-MM-DD biçiminde olmalıdır (örn: 2026-01-15).'],
    ['5. İndirim oranları yalnızca sayı; % işareti koymayın. Kademeli uygulanır:'],
    ['   20 ve 5 girilirse efektif indirim %24 olur.'],
    ['6. Durum: Aktif, Pasif veya Gelecek.'],
    ['7. Tek seferde en fazla 500 satır yüklenebilir.'], [''],
    ['KULLANILABİLİR ÜRÜN GRUPLARI:'], ...grupSatirlari, [''],
    ['NOT: Yükleme öncesi tüm satırlar sunucuda doğrulanır. Bir satırda bile hata'],
    ['varsa hiçbir kayıt yazılmaz; önce hataları düzeltip tekrar deneyin.'],
  ];
  const ornek = [
    ['ELEKTRİK KAMPANYA', 'grup indirimi', '27707', 'Ürün Grubu', 'Elektrik',
      '22', '5', '', '', '', '2026-01-01', '2040-12-01', 'Aktif'],
    ['DİSK BALATA İNDİRİM', 'tek ürün', '', 'Ürün', '0986494576',
      '10', '', '', '', '', '2026-01-01', '2026-12-31', 'Aktif'],
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(talimat), 'Talimatlar');
  const ws = XLSX.utils.aoa_to_sheet([BASLIK, ...ornek]);
  ws['!cols'] = BASLIK.map((b) => ({ wch: b.length > 18 ? 26 : 16 }));
  XLSX.utils.book_append_sheet(wb, ws, 'Kampanyalar');
  XLSX.writeFile(wb, 'as_otomotiv_toplu_kampanya_sablonu.xlsx');
}

export default function BulkCampaignModal({ acik, gruplar, onKapat, onYuklendi }) {
  const [dosya, setDosya] = useState(null);
  const [satirlar, setSatirlar] = useState([]);
  const [rapor, setRapor] = useState(null);
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState(null);
  const girdi = useRef(null);

  const sifirla = useCallback(() => {
    setDosya(null); setSatirlar([]); setRapor(null); setHata(null);
    if (girdi.current) girdi.current.value = null;
  }, []);

  useEffect(() => { if (acik) sifirla(); }, [acik, sifirla]);

  useEffect(() => {
    if (!acik) return undefined;
    const esc = (e) => { if (e.key === 'Escape' && !mesgul) onKapat(); };
    const onceki = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = onceki;
      document.removeEventListener('keydown', esc);
    };
  }, [acik, mesgul, onKapat]);

  const dosyaSecildi = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setDosya(f); setRapor(null); setHata(null); setMesgul(true);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array', cellDates: true });
      const ad = wb.SheetNames.indexOf('Kampanyalar') >= 0 ? 'Kampanyalar' : wb.SheetNames[0];
      const veri = satirlariCoz(wb.Sheets[ad]);
      if (!veri.length) throw new Error('Dosyada veri satırı bulunamadı');
      if (veri.length > 500) throw new Error('Tek seferde en fazla 500 satır yüklenebilir');
      setSatirlar(veri);
      const v = await adminCampaignApi.toplu(veri, false);
      if (!v || !v.success) throw new Error((v && v.message) || 'Doğrulama yapılamadı');
      setRapor(v);
    } catch (x) {
      setHata((x && x.message) || 'Dosya okunamadı');
      setSatirlar([]);
    } finally {
      setMesgul(false);
    }
  };

  const yukle = async () => {
    if (mesgul || !rapor || rapor.errors > 0) return;
    setMesgul(true); setHata(null);
    try {
      const v = await adminCampaignApi.toplu(satirlar, true);
      if (!v || !v.success) throw new Error((v && v.message) || 'Yükleme başarısız');
      onYuklendi(v.message || 'Kampanyalar yüklendi');
    } catch (x) {
      setHata((x && x.message) || 'Yükleme başarısız');
    } finally {
      setMesgul(false);
    }
  };

  if (!acik) return null;
  const hatali = rapor ? rapor.errors > 0 : false;

  return (
    <div className={s.overlay} role="dialog" aria-modal="true" aria-label="Toplu kampanya yükleme">
      <div className={s.backdrop} onClick={() => !mesgul && onKapat()} aria-hidden="true" />
      <div className={s.modal}>
        <div className={s.head}>
          <span className={s.headIcon}><FileSpreadsheet size={19} /></span>
          <div className={s.headText}>
            <h3 className={s.title}>Toplu Kampanya Yükleme</h3>
            <p className={s.sub}>Excel dosyasıyla çok sayıda kampanya tanımlayın</p>
          </div>
          <button type="button" className={s.closeBtn} onClick={onKapat}
            disabled={mesgul} aria-label="Kapat"><X size={18} /></button>
        </div>

        <div className={s.body}>
          {hata && <div className={s.error}><AlertTriangle size={16} /> <span>{hata}</span></div>}

          <div className={s.step}>
            <span className={s.stepNo}>1</span>
            <div className={s.stepBody}>
              <b>Şablonu indirin</b>
              <p className={s.stepText}>Talimatlar ve kullanılabilir ürün grupları dosyanın içinde.</p>
              <button type="button" className={s.ghostBtn} onClick={() => sablonIndir(gruplar)}>
                <Download size={15} /> Excel Şablonu İndir
              </button>
            </div>
          </div>

          <div className={s.step}>
            <span className={s.stepNo}>2</span>
            <div className={s.stepBody}>
              <b>Doldurduğunuz dosyayı seçin</b>
              <p className={s.stepText}>Satırlar sunucuda doğrulanır, hiçbir kayıt henüz yazılmaz.</p>
              <input ref={girdi} id="toplu-kmp-dosya" type="file" accept=".xlsx,.xls"
                className={s.fileInput} onChange={dosyaSecildi} disabled={mesgul} />
              <label htmlFor="toplu-kmp-dosya" className={s.fileLabel}>
                <Upload size={15} /> {dosya ? dosya.name : 'Dosya seçin (.xlsx, .xls)'}
              </label>
              {mesgul && !rapor && (
                <span className={s.busy}><Loader2 size={14} className={s.spin} /> Doğrulanıyor…</span>
              )}
            </div>
          </div>

          {rapor && (
            <div className={s.step}>
              <span className={s.stepNo}>3</span>
              <div className={s.stepBody}>
                <b>Sonucu kontrol edin</b>
                <div className={s.counts}>
                  <span className={s.cOk}><CheckCircle2 size={14} /> {formatNumber(rapor.valid)} geçerli</span>
                  {rapor.errors > 0 && (
                    <span className={s.cErr}><AlertTriangle size={14} /> {formatNumber(rapor.errors)} hatalı</span>
                  )}
                  {rapor.warnings > 0 && (
                    <span className={s.cWarn}><Info size={14} /> {formatNumber(rapor.warnings)} uyarı</span>
                  )}
                </div>
                {hatali && (
                  <p className={s.blockNote}>
                    Hatalı satırlar düzeltilmeden yükleme yapılamaz. Hiçbir kayıt yazılmadı.
                  </p>
                )}
                <div className={s.tableWrap}>
                  <table className={s.table}>
                    <thead>
                      <tr>
                        <th className={s.th}>Satır</th>
                        <th className={s.th}>Kampanya</th>
                        <th className={s.th}>Müşteri</th>
                        <th className={s.th}>Hedef</th>
                        <th className={s.th}>Oranlar</th>
                        <th className={s.th}>Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rapor.rows.map((r) => (
                        <tr key={r.row}
                          className={!r.ok ? s.rowErr : (r.warning ? s.rowWarn : '')}>
                          <td className={s.td}>{r.row}</td>
                          <td className={s.td}>{r.name || '-'}</td>
                          <td className={s.td}>
                            {r.ok ? (r.code ? `${r.code} — ${r.customer || ''}` : 'GENEL') : '-'}
                          </td>
                          <td className={s.td}>
                            {r.ok ? <>{r.target}{r.target_name ? <span className={s.dim}> · {r.target_name}</span> : null}</> : '-'}
                          </td>
                          <td className={s.td}>{r.ok ? (r.rates || []).join(' + ') : '-'}</td>
                          <td className={s.td}>
                            {!r.ok ? <span className={s.errText}>{r.message}</span>
                              : r.warning ? <span className={s.warnText}>{r.warning}</span>
                                : <span className={s.okText}>{r.status}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={s.foot}>
          {rapor && <button type="button" className={s.cancelBtn} onClick={sifirla}
            disabled={mesgul}>Başka dosya</button>}
          <button type="button" className={s.cancelBtn} onClick={onKapat} disabled={mesgul}>
            Kapat
          </button>
          <button type="button" className={s.saveBtn} onClick={yukle}
            disabled={mesgul || !rapor || hatali}>
            {mesgul ? <Loader2 size={16} className={s.spin} /> : <Upload size={16} />}
            {mesgul ? 'Yükleniyor…'
              : rapor ? `${formatNumber(rapor.valid)} kampanyayı yükle` : 'Yükle'}
          </button>
        </div>
      </div>
    </div>
  );
}
